/**
 * 목록 정렬 — 보기마다 같은 모양의 선택을 주되, **무엇을 기준으로 정렬하는지는 보기가 정한다.**
 *
 * 항목 종류마다 "등록 시점"에 해당하는 값이 다르고 정밀도도 다르기 때문이다.
 *
 * | 항목 | 기준 필드 | 정밀도 |
 * |---|---|---|
 * | 에픽 | 스펙 폴더 경로의 `year`/`month` | 월 |
 * | 이슈·인시던트 | `reportedAt` | 일 |
 * | 개발요청 | `receivedAt`(최초 접수) | 초 |
 * | task | **등록 시점이 없다** — `updatedAt`으로 "최근 갱신"만 낼 수 있다 | 초 |
 *
 * task에 등록일 선택지를 두지 않는 이유가 이것이다. 없는 값을 있는 것처럼 라벨만 붙이면 읽는 사람이
 * 그 순서를 등록 순서로 믿는다.
 */
export type SortKey = 'default' | 'date-desc' | 'date-asc';

export interface SortOption {
  key: SortKey;
  label: string;
}

export class DateSort {
  /**
   * 날짜 기준 정렬. `default`는 들어온 순서를 그대로 둔다 — 정렬하지 않는 것도 선택지다.
   *
   * `dateOf`가 비거나 `null`이면 **항상 뒤로 보낸다**(오름·내림 무관). 날짜 없는 항목을 가장 오래된
   * 것으로 취급하면 목록 맨 앞을 차지해 실제로 오래된 항목을 가린다.
   *
   * 같은 날짜는 `tieOf`로 순서를 고정한다 — 안 그러면 색인이 돌 때마다 같은 날 항목의 앞뒤가 바뀐다.
   */
  static sort<T>(
    items: readonly T[],
    sort: SortKey,
    dateOf: (item: T) => string | null,
    tieOf: (item: T) => string,
  ): T[] {
    if (sort === 'default') return [...items];

    const direction = sort === 'date-desc' ? -1 : 1;
    return [...items].sort((left, right) => {
      const leftDate = dateOf(left) ?? '';
      const rightDate = dateOf(right) ?? '';
      if (leftDate === '' || rightDate === '') {
        if (leftDate === rightDate) return tieOf(left).localeCompare(tieOf(right));
        return leftDate === '' ? 1 : -1;
      }
      const compared = leftDate.localeCompare(rightDate);
      return compared !== 0 ? compared * direction : tieOf(left).localeCompare(tieOf(right));
    });
  }

  /** 보기마다 라벨이 다르다 — 에픽은 "등록일", task는 "갱신"이다. 기준이 다르면 말도 달라야 한다. */
  static options(noun: string): ReadonlyArray<SortOption> {
    return [
      { key: 'default', label: '기본 순서' },
      { key: 'date-desc', label: `${noun} 최신순` },
      { key: 'date-asc', label: `${noun} 오래된순` },
    ];
  }
}
